'use client';

import Image from 'next/image';
import { useCallback, useState } from 'react';

import { SHOWREEL } from '@/content/showreel';
import { cn } from '@/lib/utils';

import { ReelVideoDialog } from './ReelLightbox';

interface ReelThumbButtonProps {
  /** Visible line next to the thumbnail. */
  label: string;
  /** `data-analytics-location` for the trigger. */
  analyticsLocation: string;
  className?: string;
}

/**
 * Compact showreel trigger: a small poster thumbnail with a play glyph, a
 * label and the running time. Nothing plays or downloads until it is
 * clicked, so it is safe inside a hero on a phone, where an autoplaying
 * teaser would spend the visitor's data on the first screen.
 */
export function ReelThumbButton({ label, analyticsLocation, className }: ReelThumbButtonProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        // Name from the visible caption + duration (WCAG 2.5.3 label-in-name);
        // an aria-label with different wording hid the caption from speech users.
        data-analytics-cta={`${analyticsLocation}_reel_open`}
        data-analytics-location={analyticsLocation}
        className={cn(
          'group flex items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          className,
        )}
      >
        <span className="relative block h-[45px] w-[80px] shrink-0 overflow-hidden rounded-md ring-1 ring-foreground/15">
          <Image
            src={SHOWREEL.posterSrc}
            alt=""
            fill
            sizes="80px"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <span className="absolute inset-0 flex items-center justify-center bg-black/25">
            <span className="flex size-6 items-center justify-center rounded-full bg-accent transition-transform duration-300 group-hover:scale-110">
              <svg viewBox="0 0 24 24" fill="currentColor" className="ml-0.5 size-3 text-background" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
        </span>
        <span className="flex flex-col">
          <span className="text-[14px] font-light leading-tight text-foreground underline-offset-4 group-hover:underline sm:text-[15px]">
            {label}
          </span>
          <span className="text-[12px] font-light text-foreground/60">{SHOWREEL.durationLabel}</span>
        </span>
      </button>

      {open && (
        <ReelVideoDialog
          src={SHOWREEL.videoSrc}
          mobileSrc={SHOWREEL.mobileVideoSrc}
          poster={SHOWREEL.posterSrc}
          ariaLabel={SHOWREEL.ariaLabel}
          onClose={close}
        />
      )}
    </>
  );
}
